import type { PrefetchVariant } from '~/core/entities/property';
import type { ShortVideoProperty } from '~/core/entities/property';
import type {
	FeaturedVideosRecommendationType,
	ShortsFilters
} from '~/core/entities/property/filters';

import { mapPropertyToShortVideo } from '~/composables/shorts/mappers';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import {
	hasRecommendationReference,
	withDisplayInHomeShortsFallback
} from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { SHORTS_SORT_BY } from '~/core/entities/property/filters';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

export interface ShortVideo {
	property: ShortVideoProperty;
	videoUrl: string | null;
	thumbnailUrl: string | null;
	images?: string[];
	videoWidth?: number;
	videoHeight?: number;
	/**
	 * The HLS ladder resolved at publish time. Lets the warm-up skip the
	 * manifest chain and pick the rung that fits the measured bandwidth; absent
	 * on videos published before the field existed, and then the warm-up
	 * discovers the ladder as before.
	 */
	prefetchVariants?: PrefetchVariant[];
}

export interface FetchShortsResult {
	items: ShortVideo[];
	hasMore: boolean;
	allWatched?: boolean;
	totalAvailable?: number;
	recommendationCountVideos?: number;
	recommendationCountPhotos?: number;
	sectionTitle?: string;
	recommendationType?: FeaturedVideosRecommendationType;
	appliedFilters: ShortsFilters;
}

const PER_PAGE = 15;

export interface FetchShortsOptions {
	enableRecommendationFallback?: boolean;
}

// Intent prefetch: a hover on a shorts entry point can start the
// featured-videos request before navigation, and the page's own fetchVideos
// call (same filters/page/perPage) reuses the in-flight promise instead of
// re-requesting. Client-only, single entry, short TTL — a stale or mismatched
// prefetch is simply ignored and the normal fetch happens.
const PREFETCH_TTL_MS = 30_000;
let feedPrefetch: {
	key: string;
	promise: Promise<FetchShortsResult>;
	ts: number;
} | null = null;

function feedPrefetchKey(
	filters: ShortsFilters,
	page: number,
	perPage: number
): string {
	return JSON.stringify([filters, page, perPage]);
}

function consumeFeedPrefetch(key: string): Promise<FetchShortsResult> | null {
	if (!import.meta.client || !feedPrefetch) return null;
	const { key: cachedKey, promise, ts } = feedPrefetch;
	if (cachedKey !== key || Date.now() - ts > PREFETCH_TTL_MS) return null;
	feedPrefetch = null;
	return promise;
}

interface InternalFetchShortsResult extends FetchShortsResult {
	hasRequestError: boolean;
}

export function useFetchShorts() {
	const logger = useLogger('useFetchShorts');
	const { user } = useUserSession();
	const getUserId = () => {
		if (!import.meta.client) return undefined;
		return user.value?.id;
	};

	const getCookieData = useCookieData();

	async function requestVideos(
		filters: ShortsFilters,
		page: number,
		perPage: number
	): Promise<InternalFetchShortsResult> {
		const [result, error] = await nuxtPropertiesRepository.getShorts(
			{ ...filters, page, perPage },
			getCookieData()
		);

		if (error || !result) {
			logger.error('Failed to fetch shorts', {
				...(error
					? extractErrorLogContext(error)
					: { error_message: 'No shorts result returned from repository' }),
				filters
			});
			return {
				items: [],
				hasMore: false,
				appliedFilters: filters,
				hasRequestError: true
			};
		}

		const items = (result.properties ?? [])
			.map(mapPropertyToShortVideo)
			.filter((item): item is ShortVideo => item !== null);

		const pagination = result.pagination;
		const hasMorePages =
			(pagination?.page ?? 1) < (pagination?.totalPages ?? 1);

		return {
			items,
			hasMore: hasMorePages,
			allWatched: result.allWatched ?? Boolean(pagination && !hasMorePages),
			totalAvailable:
				result.totalAvailable ?? pagination?.filteredCount ?? items.length,
			recommendationCountVideos: result.counts?.totalWithVideos,
			recommendationCountPhotos: result.counts?.totalWithoutVideos,
			sectionTitle: result.sectionTitle,
			recommendationType: result.recommendationType,
			appliedFilters: filters,
			hasRequestError: false
		};
	}

	async function fetchVideos(
		filters: ShortsFilters = {},
		page: number = 1,
		perPage?: number,
		options: FetchShortsOptions = {}
	): Promise<FetchShortsResult> {
		const effectivePerPage = perPage ?? PER_PAGE;

		const prefetched = consumeFeedPrefetch(
			feedPrefetchKey(filters, page, effectivePerPage)
		);
		if (prefetched) return prefetched;

		const primaryResult = await requestVideos(filters, page, effectivePerPage);

		if (
			!primaryResult.hasRequestError &&
			!primaryResult.items.length &&
			options.enableRecommendationFallback &&
			filters.sortBy !== SHORTS_SORT_BY.PRIORITY &&
			hasRecommendationReference(filters)
		) {
			return requestVideos(
				withDisplayInHomeShortsFallback(filters),
				page,
				effectivePerPage
			);
		}

		return primaryResult;
	}

	/**
	 * Starts the featured-videos request ahead of a likely navigation (e.g.
	 * hover on the header shorts button). Fire-and-forget: the result is only
	 * held for a fetchVideos call with identical filters/page/perPage within
	 * the TTL; on failure the entry clears itself so the real navigation
	 * always falls back to a normal fetch.
	 */
	function prefetchVideos(
		filters: ShortsFilters = {},
		page: number = 1,
		perPage?: number,
		options: FetchShortsOptions = {}
	): void {
		if (!import.meta.client) return;

		const effectivePerPage = perPage ?? PER_PAGE;
		const key = feedPrefetchKey(filters, page, effectivePerPage);
		if (
			feedPrefetch?.key === key &&
			Date.now() - feedPrefetch.ts <= PREFETCH_TTL_MS
		) {
			return;
		}

		const promise = fetchVideos(filters, page, effectivePerPage, options);
		feedPrefetch = { key, promise, ts: Date.now() };
		// requestVideos reports failures via hasRequestError instead of
		// throwing — drop those too, so an error result is never replayed to
		// the real navigation.
		promise
			.then(result => {
				if (
					(result as InternalFetchShortsResult).hasRequestError &&
					feedPrefetch?.key === key
				) {
					feedPrefetch = null;
				}
			})
			.catch(() => {
				if (feedPrefetch?.key === key) feedPrefetch = null;
			});
	}

	return { fetchVideos, prefetchVideos };
}
